import React, { useState, useEffect, Suspense } from 'react';
import { useBusiness } from '../context/BusinessContext';
import { api } from '../services/api';
import { MessageSquare, Database, Settings as SettingsIcon, Monitor, User, LogOut, Trash2, ChevronUp, ChevronDown, Code, Menu, X, BarChart, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import owlLogo from '../Assets/logo icon transparent background.png';
import { useAuth } from '../context/AuthContext';
import BusinessSetup from './BusinessSetup';
import PlaygroundSection from './sections/PlaygroundSection';

// Preload the Settings section since it's the default
//const SettingsSection = React.lazy(() => import('./sections/SettingsSection'));

const SuperOwlApp = () => {
  // Set default section to 'playground'
  const [activeSection, setActiveSection] = useState('playground');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // Add this line
  const [showAccountSettings, setShowAccountSettings] = useState(false); // can be removed if not used elsewhere
  const { currentBusiness, setCurrentBusiness } = useBusiness();
  const { signOut, user } = useAuth(); // Add user from useAuth
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);  // Add this state

  // Lazy load other components only when needed
  const [components, setComponents] = useState({
    playground: () => PlaygroundSection
  });

  const loadComponent = async (section) => {
    if (!components[section]) {
      let Component;
      switch (section) {
        case 'knowledgeBase':
          Component = React.lazy(() => import('./sections/KnowledgeBaseSection'));
          break;
        case 'conversations':
          Component = React.lazy(() => import('./sections/ConversationsSection'));
          break;
        case 'widget':
          Component = React.lazy(() => import('./sections/WidgetConfigSection'));
          break;
        case 'analytics':
          Component = React.lazy(() => import('./sections/AnalyticsSection'));
          break;
        case 'businessProfile':
          Component = React.lazy(() => import('./sections/BusinessProfileSection'));
          break;
        case 'playground':
          Component = PlaygroundSection;
          break;
        default:
          return;
      }
      setComponents(prev => ({ ...prev, [section]: () => Component }));
    }
  };

  // Handle section changes
  const handleSectionChange = (section) => {
    loadComponent(section);
    setActiveSection(section);
    if (window.innerWidth < 768) {  // Only close on mobile
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    // Only fetch business data if we don't have it yet
    if (!currentBusiness && user?.email) {
      const loadBusinessData = async () => {
        try {
          const response = await api.getBusinessByEmail(user.email);
          if (response.data) {
            setCurrentBusiness(response.data);
          }
        } catch (error) {
          console.error('Error loading business:', error);
        }
      };
      loadBusinessData();
    }
  }, [user?.email, currentBusiness, setCurrentBusiness]); // Add setCurrentBusiness to deps

  const renderContent = () => {
    const Component = components[activeSection]?.();
    if (!Component) return null;

    return (
      <Suspense 
        fallback={
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#3DA3DC] border-t-transparent"></div>
          </div>
        }
      >
        <Component />
      </Suspense>
    );
  };

  const handleLogout = async () => {
    try {
      // Clear business state first
      setCurrentBusiness(null);
      localStorage.removeItem('currentBusiness');
      
      // Sign out from Firebase
      await signOut();
      
      // Navigate to home page instead of login
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  const handleDeleteBusiness = async () => {
    if (!currentBusiness?.business_id) return;
    setShowDeleteModal(true); // Show modal instead of window.confirm
    setShowProfileMenu(false); // Close the profile menu
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await api.deleteBusiness(currentBusiness.business_id);
      setCurrentBusiness(null);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Delete business error:', error);
      alert('Failed to delete business. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // If there's no current business, render the BusinessSetup component
  if (!currentBusiness) {
    return <BusinessSetup />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile Sidebar Toggle - Updated positioning */}
      <button 
        className="md:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-md shadow-lg" // Changed left-4 to right-4
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6 text-gray-600" />
        ) : (
          <Menu className="w-6 h-6 text-gray-600" />
        )}
      </button>

      {/* Sidebar - Adjusted z-index */}
      <div className={`
        fixed md:static inset-y-0 left-0 transform 
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 transition duration-200 ease-in-out
        w-72 bg-white border-r border-gray-200 flex flex-col z-40
      `}>
        <div className="p-4 border-b border-gray-200 flex items-center">
          <img src={owlLogo} alt="SuperOwl Logo" className="h-8 md:h-12 mr-2 md:mr-3" />
          <div>
            <span className="text-[#1d2840] font-bold text-lg md:text-xl">Super</span>
            <span className="text-[#3DA3DC] font-bold text-lg md:text-xl">Owl</span>
          </div>
        </div>
        
        <nav className="flex-1 px-2 py-4 space-y-1">
          <SidebarItem 
            icon={<Database className="w-5 h-5" />} 
            text="Knowledge Base" 
            active={activeSection === 'knowledgeBase'} 
            onClick={() => handleSectionChange('knowledgeBase')} 
          />
          <SidebarItem 
            icon={<MessageSquare className="w-5 h-5" />} 
            text=" (Activity) Conversations" 
            active={activeSection === 'conversations'} 
            onClick={() => handleSectionChange('conversations')} 
          />
          {/* Move Business Profile above Playground */}
          <SidebarItem 
            icon={<UserCog className="w-5 h-5" />}
            text="Business Profile"
            active={activeSection === 'businessProfile'}
            onClick={() => handleSectionChange('businessProfile')}
          />
          <SidebarItem 
            icon={<Monitor className="w-5 h-5" />} 
            text="Playground / Instructions" 
            active={activeSection === 'playground'} 
            onClick={() => handleSectionChange('playground')} 
          />
          <SidebarItem 
            icon={<Code className="w-5 h-5" />}
            text="Settings"
            active={activeSection === 'widget'}
            onClick={() => handleSectionChange('widget')}
          />
          <SidebarItem 
            icon={<BarChart className="w-5 h-5" />} 
            text="Analytics" 
            active={activeSection === 'analytics'} 
            onClick={() => handleSectionChange('analytics')} 
          />
        </nav>

        {/* Profile Section */}
        <div className="relative p-3 md:p-4 border-t border-gray-200 mt-auto">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] rounded-full flex items-center justify-center text-white">
              <User className="w-3 h-3 md:w-4 md:h-4" />
            </div>
            <div className="ml-2 flex-1 min-w-0">
              <div className="text-[#1d2840] text-xs md:text-sm font-medium truncate">
                {currentBusiness?.name || 'No Business Selected'}
              </div>
              <div className="text-[10px] md:text-xs text-gray-500 truncate">
                {/* Show email instead of business ID */}
                {currentBusiness?.email ? `Email: ${currentBusiness.email}` : 'Email: N/A'}
              </div>
            </div>
            {showProfileMenu ? (
              <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
            )}
          </div>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute bottom-full left-0 w-full mb-2 bg-white border border-gray-200 rounded-md shadow-lg py-1">
              <button
                // Open account settings in a new page
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/account-settings');
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <UserCog className="w-4 h-4 mr-2" />
                Account Settings
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Overlay for mobile sidebar */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      
      {/* Main Content Area - Fixed new layout */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full">
            {/* REMOVE Account Settings Modal */}
            {/* Main Content */}
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Delete Business</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your business? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium flex items-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarItem = ({ icon, text, active, onClick }) => (
  <div 
    className={`flex items-center px-3 py-2 rounded-md cursor-pointer text-sm md:text-base ${
      active 
        ? 'bg-[#3DA3DC]/10 text-[#3DA3DC]' 
        : 'text-gray-700 hover:bg-gray-100'
    }`}
    onClick={onClick}
  >
    <div className={active ? 'text-[#3DA3DC]' : 'text-gray-500'}>
      {React.cloneElement(icon, { className: 'w-4 h-4 md:w-5 md:h-5' })}
    </div>
    <span className="ml-2 md:ml-3 font-medium truncate">{text}</span>
  </div>
);

export default SuperOwlApp;
